// ============================================================================
// REQUEST HOOKS - Digital Approval System
// ============================================================================
// React hooks for Request API operations

import { useState, useEffect, useCallback, useMemo } from 'react';
import { requestService } from 'api/services';
import type {
  Request,
  RequestDetails,
  CreateRequestRequest,
  UpdateRequestRequest,
  RequestQueryParameters,
  RequestListResponse,
  UpdateRequestStatusRequest,
  PaginationInfo,
  ID,
} from 'api/types';

/**
 * Hook to fetch all requests with pagination and filtering
 */
export const useRequests = (params?: RequestQueryParameters) => {
  const [data, setData] = useState<RequestListResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  // Stringify params for stable dependency comparison
  const paramsKey = useMemo(() => JSON.stringify(params), [params]);

  const fetchRequests = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await requestService.getAll(params);
      setData(response);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramsKey]);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  // Transform backend response to normalized pagination info
  const pagination: PaginationInfo | undefined = useMemo(() => {
    if (!data) return undefined;
    return {
      currentPage: data.page,
      pageSize: data.pageSize,
      totalPages: data.totalPages,
      totalItems: data.totalCount,
      hasNextPage: data.hasNext,
      hasPreviousPage: data.hasPrevious,
    };
  }, [data]);

  return {
    data,
    items: data?.items || [],
    pagination,
    loading,
    error,
    refetch: fetchRequests,
  };
};

/**
 * Hook to fetch a single request by ID
 */
export const useRequest = (id: ID | null) => {
  const [data, setData] = useState<Request | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchRequest = useCallback(async () => {
    if (!id) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const response = await requestService.getById(id);
      setData(response);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchRequest();
  }, [fetchRequest]);

  return {
    data,
    loading,
    error,
    refetch: fetchRequest,
  };
};

/**
 * Hook to fetch complete request details with approvers, attachments, and related tickets
 */
export const useRequestDetails = (id: ID | null) => {
  const [data, setData] = useState<RequestDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchRequestDetails = useCallback(async () => {
    if (!id) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const response = await requestService.getDetails(id);
      setData(response);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchRequestDetails();
  }, [fetchRequestDetails]);

  return {
    data,
    loading,
    error,
    refetch: fetchRequestDetails,
  };
};

/**
 * Hook to fetch request by request number
 */
export const useRequestByNumber = (requestNumber: string | null) => {
  const [data, setData] = useState<Request | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchRequest = useCallback(async () => {
    if (!requestNumber) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const response = await requestService.getByNumber(requestNumber);
      setData(response);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, [requestNumber]);

  useEffect(() => {
    fetchRequest();
  }, [fetchRequest]);

  return {
    data,
    loading,
    error,
    refetch: fetchRequest,
  };
};

/**
 * Hook to fetch current user's requests
 */
export const useMyRequests = () => {
  const [data, setData] = useState<Request[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchRequests = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await requestService.getMyRequests();
      setData(response);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  return {
    data,
    loading,
    error,
    refetch: fetchRequests,
  };
};

/**
 * Hook to fetch requests by status
 */
export const useRequestsByStatus = (status: string) => {
  const [data, setData] = useState<Request[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchRequests = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await requestService.getByStatus(status);
      setData(response);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, [status]);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  return {
    data,
    loading,
    error,
    refetch: fetchRequests,
  };
};

/**
 * Hook to create a new request
 */
export const useCreateRequest = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [success, setSuccess] = useState(false);

  const createRequest = async (
    data: CreateRequestRequest,
    options?: {
      onSuccess?: (request: Request) => void;
      onError?: (error: Error) => void;
    }
  ) => {
    setLoading(true);
    setError(null);
    setSuccess(false);

    try {
      const response = await requestService.create(data);
      setSuccess(true);
      if (options?.onSuccess) {
        options.onSuccess(response);
      }
      return response;
    } catch (err) {
      const error = err as Error;
      setError(error);
      if (options?.onError) {
        options.onError(error);
      }
      throw error;
    } finally {
      setLoading(false);
    }
  };

  return {
    createRequest,
    loading,
    error,
    success,
  };
};

/**
 * Hook to update an existing request
 */
export const useUpdateRequest = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [success, setSuccess] = useState(false);

  const updateRequest = async (
    id: ID,
    data: UpdateRequestRequest,
    options?: {
      onSuccess?: (request: Request) => void;
      onError?: (error: Error) => void;
    }
  ) => {
    setLoading(true);
    setError(null);
    setSuccess(false);

    try {
      const response = await requestService.update(id, data);
      setSuccess(true);
      if (options?.onSuccess) {
        options.onSuccess(response);
      }
      return response;
    } catch (err) {
      const error = err as Error;
      setError(error);
      if (options?.onError) {
        options.onError(error);
      }
      throw error;
    } finally {
      setLoading(false);
    }
  };

  return {
    updateRequest,
    loading,
    error,
    success,
  };
};

/**
 * Hook to submit a draft request
 */
export const useSubmitRequest = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [success, setSuccess] = useState(false);

  const submitRequest = async (
    id: ID,
    options?: {
      onSuccess?: (request: Request) => void;
      onError?: (error: Error) => void;
    }
  ) => {
    setLoading(true);
    setError(null);
    setSuccess(false);

    try {
      const response = await requestService.submit(id);
      setSuccess(true);
      if (options?.onSuccess) {
        options.onSuccess(response);
      }
      return response;
    } catch (err) {
      const error = err as Error;
      setError(error);
      if (options?.onError) {
        options.onError(error);
      }
      throw error;
    } finally {
      setLoading(false);
    }
  };

  return {
    submitRequest,
    loading,
    error,
    success,
  };
};

/**
 * Hook to update request status
 */
export const useUpdateRequestStatus = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [success, setSuccess] = useState(false);

  const updateStatus = async (
    id: ID,
    data: UpdateRequestStatusRequest,
    options?: {
      onSuccess?: () => void;
      onError?: (error: Error) => void;
    }
  ) => {
    setLoading(true);
    setError(null);
    setSuccess(false);

    try {
      await requestService.updateStatus(id, data);
      setSuccess(true);
      if (options?.onSuccess) {
        options.onSuccess();
      }
    } catch (err) {
      const error = err as Error;
      setError(error);
      if (options?.onError) {
        options.onError(error);
      }
      throw error;
    } finally {
      setLoading(false);
    }
  };

  return {
    updateStatus,
    loading,
    error,
    success,
  };
};

/**
 * Hook to delete a request
 */
export const useDeleteRequest = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [success, setSuccess] = useState(false);

  const deleteRequest = async (
    id: ID,
    options?: {
      onSuccess?: () => void;
      onError?: (error: Error) => void;
    }
  ) => {
    setLoading(true);
    setError(null);
    setSuccess(false);

    try {
      await requestService.delete(id);
      setSuccess(true);
      if (options?.onSuccess) {
        options.onSuccess();
      }
    } catch (err) {
      const error = err as Error;
      setError(error);
      if (options?.onError) {
        options.onError(error);
      }
      throw error;
    } finally {
      setLoading(false);
    }
  };

  return {
    deleteRequest,
    loading,
    error,
    success,
  };
};
